import { useCallback, useMemo } from "react";
import { useOrganization } from "@/hooks/useOrganization";

// Definição das keys de módulos disponíveis
export const MODULE_KEYS = {
  DASHBOARD: "dashboard",
  LEADS: "leads",
  GOALS: "goals",
  GOALS_DEPARTMENTS: "goals_departments",
  GOALS_VENDEDORES: "goals_vendedores",
  FAROL: "farol",
  TASKS: "tasks",
  TEAM: "team",
  COURSES: "courses",
  DISC: "disc",
  EMAIL_MARKETING: "email_marketing",
  OUTREACH_FLOWS: "outreach_flows",
  AI_ASSISTANT: "ai_assistant",
  WHATSAPP: "whatsapp",
  WHATSAPP_CHAT: "whatsapp_chat", // Módulo separado para Chat (planos médio e completo)
  ORG_SETTINGS: "org_settings",
  MEETINGS: "meetings",
  REMINDERS: "reminders",
  AUTOMATIONS: "automations", // Módulo de automações (inicialmente só JNR Marketing)
} as const;

export type ModuleKey = typeof MODULE_KEYS[keyof typeof MODULE_KEYS];

// ========================================
// PLANOS FECHADOS - SEM EDIÇÃO INDIVIDUAL
// ========================================

// Tipos de planos disponíveis (SEM "personalizado")
export type PlanType = "basico" | "medio" | "completo";

// Presets de planos - IMUTÁVEIS
export const PLAN_PRESETS: Record<PlanType, readonly string[]> = {
  // BÁSICO: Core CRM + WhatsApp + Equipe + Cursos
  basico: [
    MODULE_KEYS.DASHBOARD,
    MODULE_KEYS.LEADS,
    MODULE_KEYS.WHATSAPP,
    MODULE_KEYS.TEAM,
    MODULE_KEYS.ORG_SETTINGS,
    MODULE_KEYS.COURSES, // Cursos disponível em todos os planos
  ] as const,
  
  // MÉDIO: Básico + Metas + Produtividade + Chat WhatsApp + Automações (sem IA, sem Farol)
  medio: [
    MODULE_KEYS.DASHBOARD,
    MODULE_KEYS.LEADS,
    MODULE_KEYS.WHATSAPP,
    MODULE_KEYS.WHATSAPP_CHAT, // Chat disponível no plano médio
    MODULE_KEYS.TEAM,
    MODULE_KEYS.ORG_SETTINGS,
    // Adições do médio (IA e Farol apenas no Completo)
    MODULE_KEYS.GOALS,
    MODULE_KEYS.GOALS_VENDEDORES,
    MODULE_KEYS.GOALS_DEPARTMENTS,
    MODULE_KEYS.TASKS,
    MODULE_KEYS.MEETINGS,
    MODULE_KEYS.REMINDERS,
    MODULE_KEYS.COURSES, // Cursos disponível em todos os planos
    MODULE_KEYS.AUTOMATIONS, // Automações disponível no plano médio e completo
  ] as const,
  
  // COMPLETO: Todos os módulos
  completo: [
    MODULE_KEYS.DASHBOARD,
    MODULE_KEYS.LEADS,
    MODULE_KEYS.WHATSAPP,
    MODULE_KEYS.WHATSAPP_CHAT, // Chat disponível no plano completo
    MODULE_KEYS.TEAM,
    MODULE_KEYS.ORG_SETTINGS,
    MODULE_KEYS.AI_ASSISTANT,
    MODULE_KEYS.GOALS,
    MODULE_KEYS.GOALS_VENDEDORES,
    MODULE_KEYS.GOALS_DEPARTMENTS,
    MODULE_KEYS.FAROL,
    MODULE_KEYS.TASKS,
    MODULE_KEYS.MEETINGS,
    MODULE_KEYS.REMINDERS,
    MODULE_KEYS.COURSES,
    MODULE_KEYS.DISC,
    MODULE_KEYS.EMAIL_MARKETING,
    MODULE_KEYS.OUTREACH_FLOWS,
    MODULE_KEYS.AUTOMATIONS, // Automações disponível no plano médio e completo
  ] as const,
};

// Labels amigáveis dos planos
export const PLAN_LABELS: Record<PlanType, { emoji: string; label: string; description: string }> = {
  basico: {
    emoji: "🌱",
    label: "Básico",
    description: "CRM essencial com WhatsApp e gestão de equipe",
  },
  medio: {
    emoji: "🚀",
    label: "Médio",
    description: "Inclui IA, metas, tarefas e reuniões",
  },
  completo: {
    emoji: "⭐",
    label: "Completo",
    description: "Todos os módulos disponíveis",
  },
};

// ========================================
// LIMITES DE LEADS ATIVOS POR PLANO
// ========================================

export const PLAN_LIMITS: Record<PlanType, { maxActiveLeads: number; warningThreshold: number }> = {
  basico: {
    maxActiveLeads: 20000,
    warningThreshold: 0.8, // 80% = 16.000
  },
  medio: {
    maxActiveLeads: 80000,
    warningThreshold: 0.8, // 80% = 64.000
  },
  completo: {
    maxActiveLeads: 250000,
    warningThreshold: 0.8, // 80% = 200.000
  },
};

// ========================================
// DEPENDÊNCIAS DE MÓDULOS (para validação)
// ========================================

export const MODULE_DEPENDENCIES: Record<string, string[]> = {
  dashboard: ["leads"],
  goals: ["leads"],
  goals_departments: ["leads"],
  goals_vendedores: ["leads"],
  farol: ["leads"],
  ai_assistant: ["leads"],
  tasks: ["team"],
  meetings: ["team"],
  reminders: ["leads"],
  outreach_flows: ["email_marketing"],
  email_marketing: ["leads"],
};

// Função para validar dependências (útil para debug, mas os presets já são consistentes)
export function validateModuleDependencies(modules: string[]): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  for (const mod of modules) {
    const deps = MODULE_DEPENDENCIES[mod];
    if (deps) {
      for (const dep of deps) {
        if (!modules.includes(dep)) {
          errors.push(`${mod} requer ${dep}`);
        }
      }
    }
  }
  
  return { valid: errors.length === 0, errors };
}

// Configuração dos módulos para UI (exibição somente leitura)
export const MODULE_CONFIG: readonly { key: string; label: string; description: string }[] = [
  { key: MODULE_KEYS.DASHBOARD, label: "Dashboard", description: "Painel principal com métricas" },
  { key: MODULE_KEYS.LEADS, label: "CRM & Leads", description: "Gestão de leads e pipeline" },
  { key: MODULE_KEYS.WHATSAPP, label: "WhatsApp", description: "Integração WhatsApp para CRM" },
  { key: MODULE_KEYS.WHATSAPP_CHAT, label: "Chat WhatsApp", description: "Conversar pelo WhatsApp" },
  { key: MODULE_KEYS.TEAM, label: "Equipe", description: "Gestão de membros" },
  { key: MODULE_KEYS.ORG_SETTINGS, label: "Config. Org", description: "Configurações da org" },
  { key: MODULE_KEYS.AI_ASSISTANT, label: "JNR (IA)", description: "Assistente de vendas IA" },
  { key: MODULE_KEYS.GOALS, label: "Metas", description: "Acompanhamento de metas gerais" },
  { key: MODULE_KEYS.GOALS_DEPARTMENTS, label: "Metas Setoriais", description: "Metas por departamento" },
  { key: MODULE_KEYS.GOALS_VENDEDORES, label: "Metas Vendedores", description: "Metas individuais" },
  { key: MODULE_KEYS.FAROL, label: "Farol", description: "Gestão de tempo e atividades" },
  { key: MODULE_KEYS.TASKS, label: "Tarefas", description: "Gestão de tarefas da equipe" },
  { key: MODULE_KEYS.MEETINGS, label: "Reuniões", description: "Agenda de reuniões" },
  { key: MODULE_KEYS.REMINDERS, label: "Lembretes", description: "Página de lembretes" },
  { key: MODULE_KEYS.COURSES, label: "Cursos", description: "Trilhas de aprendizado" },
  { key: MODULE_KEYS.DISC, label: "DISC", description: "Testes e histórico DISC" },
  { key: MODULE_KEYS.EMAIL_MARKETING, label: "Email Marketing", description: "Templates e sequências" },
  { key: MODULE_KEYS.OUTREACH_FLOWS, label: "Fluxos de Outreach", description: "Automação multicanal" },
  { key: MODULE_KEYS.AUTOMATIONS, label: "Automações", description: "Automações de funil e distribuição" },
];

// Labels simples para exibição (key -> label)
export const MODULE_LABELS: Record<string, string> = MODULE_CONFIG.reduce((acc, m) => {
  acc[m.key] = m.label;
  return acc;
}, {} as Record<string, string>);

// Helper para obter módulos de um plano de forma type-safe
export function getModulesForPlan(planType: string): string[] {
  const validPlan = planType as PlanType;
  if (validPlan in PLAN_PRESETS) {
    return [...PLAN_PRESETS[validPlan]];
  }
  // Fallback para completo se plano inválido
  return [...PLAN_PRESETS.completo];
}

export function useOrgModules() {
  const { currentOrg } = useOrganization();

  const hasModule = useCallback((moduleKey: string): boolean => {
    const mods = currentOrg?.enabled_modules;
    
    // 1. Verificar no array enabled_modules
    if (Array.isArray(mods) && mods.includes(moduleKey)) {
      return true;
    }
    
    // 2. FALLBACK: Verificar se o plano inclui esse módulo
    // Isso cobre orgs criadas antes de módulos serem adicionados aos presets
    const plan = currentOrg?.plan_type as PlanType;
    if (plan && plan in PLAN_PRESETS) {
      const planModules = PLAN_PRESETS[plan];
      if (planModules.includes(moduleKey)) {
        return true;
      }
    }
    
    return false;
  }, [currentOrg?.enabled_modules, currentOrg?.plan_type]);

  const enabledModules = useMemo(() => {
    const mods = currentOrg?.enabled_modules;
    return Array.isArray(mods) ? mods : [];
  }, [currentOrg?.enabled_modules]);

  const planType = useMemo((): PlanType => {
    const plan = currentOrg?.plan_type;
    // Validar que é um plano válido
    if (plan === "basico" || plan === "medio" || plan === "completo") {
      return plan;
    }
    // Fallback para basico
    return "basico";
  }, [currentOrg?.plan_type]);

  const planLimits = useMemo(() => {
    return PLAN_LIMITS[planType];
  }, [planType]);

  const planLabel = useMemo(() => {
    return PLAN_LABELS[planType];
  }, [planType]);

  return { 
    hasModule, 
    enabledModules,
    planType,
    planLimits,
    planLabel,
    MODULE_KEYS,
    MODULE_CONFIG,
    PLAN_PRESETS,
    PLAN_LABELS,
    PLAN_LIMITS,
  };
}
